import React, { useRef, useState, useCallback } from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';
import { Heading, Typography } from '@fiverr-private/typography';
import { Input } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Counter } from '@fiverr-private/data_display';
import { useBriefContext } from '../../../../common/BriefContext';
import { EditWrapper } from '../EditWrapper';
import { BriefSectionActions } from '../BriefSection/BriefSectionActions';
import * as styles from '../common/styles.ve.css';
import { SHORT_INPUT_LIMIT } from '../BriefSection/SectionTypes/ShortText';

export const TITLE_SECTION_ID = 'title';

interface TitleViewModeProps {
  onEditStart: () => void;
}

const TitleViewMode = ({ onEditStart }: TitleViewModeProps) => {
  const { editedBrief, editedSectionId } = useBriefContext();

  const onClick = () => {
    if (!editedSectionId) {
      onEditStart();
    }
  };

  return (
    <Stack
      gap="1"
      padding="3"
      justifyContent="spaceBetween"
      className={classnames({
        [styles.sectionContainer]: !editedSectionId,
      })}
      cursor={!editedSectionId ? 'pointer' : 'default'}
      onClick={onClick}
    >
      <Heading size="h_md" color="heading" overflow="hidden">
        {editedBrief?.title}
      </Heading>
      <BriefSectionActions onEditStart={noop} />
    </Stack>
  );
};

interface TitleEditModeProps {
  value: string;
  onChange: (value: string) => void;
}

const TitleEditMode = ({ value, onChange }: TitleEditModeProps) => {
  const onContentChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onChange(e.target.value);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Stack gap="1.5" direction="column">
      <Input
        ref={inputRef}
        defaultValue={value}
        onChange={onContentChange}
        maxLength={SHORT_INPUT_LIMIT}
        autoFocus
        marginBottom="0.5"
      />
      <Typography size="b_sm" alignSelf="flexEnd">
        <Counter current={value.length} max={SHORT_INPUT_LIMIT} />
      </Typography>
    </Stack>
  );
};

export const BriefTitle = () => {
  const { editedBrief, updateEditedBrief, setEditedSectionId, editedSectionId, registerSectionRef } = useBriefContext();
  const [value, setValue] = useState(editedBrief?.title || '');

  const sectionRefCallback = useCallback(
    (node: HTMLElement | null) => {
      registerSectionRef(TITLE_SECTION_ID, node);
    },
    [registerSectionRef]
  );

  if (!editedBrief?.title) {
    return null;
  }

  const isEditing = editedSectionId === TITLE_SECTION_ID;

  const onEditStart = () => {
    setEditedSectionId(TITLE_SECTION_ID);
  };
  const onSave = () => {
    setEditedSectionId(null);
    updateEditedBrief({ title: value });
  };

  const onCancel = () => {
    setEditedSectionId(null);
    setValue(editedBrief?.title || '');
  };

  const editWrapper = (
    <EditWrapper
      isEditing={isEditing}
      viewMode={<TitleViewMode onEditStart={onEditStart} />}
      editMode={<TitleEditMode value={value} onChange={setValue} />}
      onCancel={onCancel}
      onEditEnd={onSave}
    />
  );

  return (
    <Container ref={sectionRefCallback}>
      {isEditing ? (
        <Container
          ref={sectionRefCallback}
          padding="3"
          backgroundColor="grey_100"
          borderColor="grey_600"
          borderRadius="xl"
          borderWidth="sm"
          borderStyle="solid"
        >
          {editWrapper}
        </Container>
      ) : (
        editWrapper
      )}
    </Container>
  );
};
